import { useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronDown, ChevronUp, Filter } from "react-feather";

import "./stylesManualActions.css";

import { SIDER_TABS } from "../../utils/Constants";
import CustomDatePicker from "../CommonComponents/CustomDatePicker/CustomDatePicker";
import { AppStrings } from "../../utils/constants/AppStrings";
import {
  canAccessAgentAuditLogTab,
} from "../../utils/constants/permissionsConstants";
import AllManualActions from "./AllManualActions/AllManualActions";
import WalletManualActions from "./Wallet/WalletManualActions";
import WithdrawalsManualActions from "./Withdrawals/WithdrawalsManualActions";
import PromosManualActions from "./Promos/PromosManualActions";
import SportsBetsManualActions from "./SportsBets/SportsBetsManualActions";
import SuspensionsManualActions from "./Suspensions/SuspensionsManualActions";
import ToggleSwitch from "../CommonComponents/ToggleSwitch/ToggleSwitch";
import { DownloadOutlined } from "@ant-design/icons";
import { useManualActionsFlags } from "../../hooks/useFeatureFlag";
import AppContext from "../../context/AppContext";
import SectionUnavailable from "../../routing/SectionUnavailable";
import { usePageTitle } from "../CommonComponents/Layout/PageTitleContext";
import { getAdminNow } from "../../utils/dateService";

const tabs = [
  { label: AppStrings.ALL, value: "all" },
  { label: AppStrings.WALLET, value: "wallet" },
  { label: AppStrings.WITHDRAWAL_REVIEW_TITLE, value: "withdrawals" },
  { label: AppStrings.PROMOS, value: "promos" },
  { label: AppStrings.SPORTS_BETS, value: "sports-bets" },
  { label: AppStrings.SUSPENSIONS, value: "suspensions" },
];

const TAB_QUERY_KEY = "tab";

function ManualActions() {
  usePageTitle(AppStrings.AGENT_AUDIT_LOG);
  const { adminPermissions } = useContext(AppContext);
  const { showSportsbookKpi } = useManualActionsFlags();
  const [searchParams, setSearchParams] = useSearchParams();

  const visibleTabs = useMemo(
    () =>
      tabs.filter((t) => {
        if (t.value === "sports-bets" && !showSportsbookKpi) return false;
        return canAccessAgentAuditLogTab(adminPermissions, t.value);
      }),
    [showSportsbookKpi, adminPermissions],
  );
  const VALID_TAB_VALUES = useMemo(
    () => new Set(visibleTabs.map((t) => t.value)),
    [visibleTabs],
  );

  // Never fall back to "all" — that tab may be outside the admin's permissions
  // (e.g. sports-bets-only when showSportsbookKpi hides the only granted tab).
  const defaultTab = useMemo(() => visibleTabs[0]?.value, [visibleTabs]);

  const activeTab = useMemo(() => {
    const raw = searchParams.get(TAB_QUERY_KEY);
    if (raw && VALID_TAB_VALUES.has(raw)) return raw;
    return defaultTab;
  }, [searchParams, VALID_TAB_VALUES, defaultTab]);

  useEffect(() => {
    if (!defaultTab) return;
    const raw = searchParams.get(TAB_QUERY_KEY);
    const shouldStrip =
      raw === defaultTab || (raw && !VALID_TAB_VALUES.has(raw));
    if (shouldStrip) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(TAB_QUERY_KEY);
          return next;
        },
        { replace: true },
      );
    }
  }, [searchParams, setSearchParams, VALID_TAB_VALUES, defaultTab]);

  const dateRange = [getAdminNow().startOf("month"), getAdminNow()];
  const [dates, setDates] = useState(dateRange);
  const [agentPerformanceView, setAgentPerformanceView] = useState(false);
  const [showKpis, setShowKpis] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState({});
  const [downloadTrigger, setDownloadTrigger] = useState(0);

  /*istanbul ignore next */
  const handleDownload = () => {
    setDownloadTrigger((prev) => prev + 1);
  };

  /*istanbul ignore next */
  const handlePreviousMonth = () => {
    const newStart = dates[0].subtract(1, "month");
    const newEnd = dates[1].subtract(1, "month");
    setDates([newStart, newEnd]);
  };

  /*istanbul ignore next */
  const handleNextMonth = () => {
    const newStart = dates[0].add(1, "month");
    const newEnd = dates[1].add(1, "month");
    setDates([newStart, newEnd]);
  };

  const handleTabChange = (tabValue) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tabValue === defaultTab) next.delete(TAB_QUERY_KEY);
        else next.set(TAB_QUERY_KEY, tabValue);
        return next;
      },
      { replace: true },
    );
    setAgentPerformanceView(false);
    setShowKpis(false);
    setFiltersOpen({});
  };

  if (visibleTabs.length === 0) {
    return (
      <>
        <SectionUnavailable />
      </>
    );
  }

  return (
    <>
      <div className="manual-actions-container">
        <div className="manual-actions-header">
          <div className="manual-actions-header-controls">
            <CustomDatePicker
              dates={dates}
              setDates={setDates}
              getData={() => {}}
              showQuickSelect={true}
              showPresets={true}
              startRange={AppStrings.THIS_MONTH}
            />
            <button className="download-button" onClick={handleDownload}>
              <DownloadOutlined />
            </button>
          </div>
        </div>
        <div className="manual-actions-header-container">
          <div className="manual-actions-header-tabs">
            {visibleTabs.map((tab) => (
              <button
                key={tab.value}
                className={tab.value === activeTab ? "active" : ""}
                onClick={() => handleTabChange(tab.value)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="manual-actions-header-controls-container">
            <div className="manual-actions-header-agent-performance-view">
              <p>{AppStrings.AGENT_PERFORMANCE_VIEW}</p>
              <ToggleSwitch
                status={agentPerformanceView}
                updateEventCode={() =>
                  setAgentPerformanceView(!agentPerformanceView)
                }
                onlyUpdate={true}
              />
            </div>

            {activeTab === "all" && !agentPerformanceView && (
              <button
                type="button"
                className="filter-buttons all-manual-actions-kpis-toggle"
                onClick={() => setShowKpis(!showKpis)}
              >
                <span>{AppStrings.SHOW_KPIS}</span>
                {showKpis ? (
                  <ChevronUp className="filter-icon" />
                ) : (
                  <ChevronDown className="filter-icon" />
                )}
              </button>
            )}

            {!agentPerformanceView && (
              <div
                className="manual-actions-wallet-filter-btn"
                data-testid="filter-toggle-btn"
                onClick={() =>
                  setFiltersOpen((prev) => ({
                    ...prev,
                    [activeTab]: !prev[activeTab],
                  }))
                }
              >
                <Filter size={18} />
                {filtersOpen[activeTab] ? (
                  <ChevronUp size={16} />
                ) : (
                  <ChevronDown size={16} />
                )}
              </div>
            )}
          </div>
        </div>

        <div className="manual-actions-body">
          {activeTab === "wallet" && (
            <WalletManualActions
              agentPerformanceView={agentPerformanceView}
              dates={dates}
              showFilters={filtersOpen["wallet"] ?? false}
              downloadTrigger={downloadTrigger}
            />
          )}
          {activeTab === "all" && (
            <AllManualActions
              activeTab={activeTab}
              agentPerformanceView={agentPerformanceView}
              showFilters={filtersOpen["all"] ?? false}
              showKpis={showKpis}
              dates={dates}
              downloadTrigger={downloadTrigger}
            />
          )}
          {activeTab === "withdrawals" && (
            <WithdrawalsManualActions
              agentPerformanceView={agentPerformanceView}
              showFilters={filtersOpen["withdrawals"] ?? false}
              dates={dates}
              downloadTrigger={downloadTrigger}
            />
          )}
          {activeTab === "promos" && (
            <PromosManualActions
              agentPerformanceView={agentPerformanceView}
              showFilters={filtersOpen["promos"] ?? false}
              dates={dates}
              downloadTrigger={downloadTrigger}
            />
          )}
          {activeTab === "sports-bets" && (
            <SportsBetsManualActions
              agentPerformanceView={agentPerformanceView}
              showFilters={filtersOpen["sports-bets"] ?? false}
              dates={dates}
              downloadTrigger={downloadTrigger}
            />
          )}
          {activeTab === "suspensions" && (
            <SuspensionsManualActions
              agentPerformanceView={agentPerformanceView}
              showFilters={filtersOpen["suspensions"] ?? false}
              dates={dates}
              downloadTrigger={downloadTrigger}
            />
          )}
        </div>
      </div>
    </>
  );
}

export default ManualActions;
